import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import DocIcon from "../../resources/icon/documnet-icon.png";
import {isImageAttachment, resolveAttachmentPreviewUrl} from "./attachmentPreviewUtils";
import "./attachmentContainer.css";

const HOVER_PREVIEW_DELAY_MS = 1000;
const CURSOR_OFFSET = 12;
const VIEWPORT_PADDING = 16;
const IMAGE_MAX_SIZE = 480;
const PANEL_CHROME_HEIGHT = 72;

function getMaxImageHeight(cursorY) {
    const availableAbove = cursorY - CURSOR_OFFSET - VIEWPORT_PADDING - PANEL_CHROME_HEIGHT;
    return Math.max(160, Math.min(IMAGE_MAX_SIZE, availableAbove));
}

function computePanelPosition(cursor, panelRect) {
    let left = cursor.x + CURSOR_OFFSET;
    let top = cursor.y - CURSOR_OFFSET;

    if (panelRect) {
        if (panelRect.top < VIEWPORT_PADDING) {
            top += VIEWPORT_PADDING - panelRect.top;
        }
        if (panelRect.left < VIEWPORT_PADDING) {
            left += VIEWPORT_PADDING - panelRect.left;
        }
        if (panelRect.right > window.innerWidth - VIEWPORT_PADDING) {
            left -= panelRect.right - (window.innerWidth - VIEWPORT_PADDING);
        }
    }

    left = Math.max(VIEWPORT_PADDING, left);

    return {left, top};
}

function AttachmentHoverPreviewPortal({preview, onClose, setPreview}) {
    const panelRef = useRef(null);
    const [panelPos, setPanelPos] = useState(null);
    const [layoutVersion, setLayoutVersion] = useState(0);

    const maxImageHeight = preview?.kind === "image"
        ? getMaxImageHeight(preview.cursor.y)
        : IMAGE_MAX_SIZE;

    const updatePanelPosition = () => {
        if (!preview || !panelRef.current) return;

        const panelRect = panelRef.current.getBoundingClientRect();
        setPanelPos(computePanelPosition(preview.cursor, panelRect));
    };

    useLayoutEffect(() => {
        if (!preview) {
            setPanelPos(null);
            return;
        }

        updatePanelPosition();
    }, [preview, preview?.cursor?.x, preview?.cursor?.y, preview?.status, preview?.imageUrl, preview?.kind, layoutVersion, maxImageHeight]);

    useEffect(() => {
        if (!preview || preview.kind !== "image" || preview.status !== "loading") return undefined;

        const currentItem = preview.item;
        let cancelled = false;

        resolveAttachmentPreviewUrl(currentItem, preview.fileName)
            .then((url) => {
                if (cancelled) {
                    URL.revokeObjectURL(url);
                    return;
                }
                setPreview((current) => (
                    current?.item === currentItem
                        ? {...current, status: "ready", imageUrl: url}
                        : current
                ));
            })
            .catch(() => {
                if (!cancelled) {
                    setPreview((current) => (
                        current?.item === currentItem
                            ? {...current, status: "failed"}
                            : current
                    ));
                }
            });

        return () => {
            cancelled = true;
        };
    }, [preview, setPreview]);

    useEffect(() => {
        if (!preview?.imageUrl) return undefined;
        const url = preview.imageUrl;
        return () => URL.revokeObjectURL(url);
    }, [preview?.imageUrl]);

    useEffect(() => {
        if (!preview) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === "Escape") onClose();
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [preview, onClose]);

    if (!preview) return null;

    const anchorLeft = preview.cursor.x + CURSOR_OFFSET;
    const anchorTop = preview.cursor.y - CURSOR_OFFSET;
    const {left, top} = panelPos ?? {left: anchorLeft, top: anchorTop};

    return createPortal(
        <div className="attch-hover-preview-layer" role="presentation" aria-hidden="true">
            <div className="attch-hover-preview__backdrop" />
            <div
                ref={panelRef}
                className="attch-hover-preview__panel"
                style={{left, top}}
            >
                {preview.kind === "file" && (
                    <img src={DocIcon} alt="" className="attch-hover-preview__file-icon" />
                )}
                {preview.kind === "image" && preview.status === "loading" && (
                    <div
                        className="attch-hover-preview__loading"
                        style={{minHeight: Math.min(280, maxImageHeight)}}
                    >
                        미리보기 로딩 중…
                    </div>
                )}
                {preview.kind === "image" && preview.status === "ready" && preview.imageUrl && (
                    <img
                        src={preview.imageUrl}
                        alt={preview.fileName}
                        className="attch-hover-preview__image"
                        style={{maxHeight: maxImageHeight}}
                        onLoad={() => setLayoutVersion((value) => value + 1)}
                    />
                )}
                {preview.kind === "image" && preview.status === "failed" && (
                    <img src={DocIcon} alt="" className="attch-hover-preview__file-icon" />
                )}
                <p className="attch-hover-preview__caption" title={preview.fileName}>
                    {preview.fileName}
                </p>
            </div>
        </div>,
        document.body,
    );
}

export function useAttachmentHoverPreview(enabled) {
    const timerRef = useRef(null);
    const activeRowRef = useRef(null);
    const moveHandlerRef = useRef(null);
    const cursorRef = useRef({x: 0, y: 0});
    const [preview, setPreview] = useState(null);

    const clearPreviewTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const detachRowTracking = () => {
        if (activeRowRef.current && moveHandlerRef.current) {
            activeRowRef.current.removeEventListener("mousemove", moveHandlerRef.current);
        }
        activeRowRef.current = null;
        moveHandlerRef.current = null;
    };

    const closePreview = () => {
        clearPreviewTimer();
        detachRowTracking();
        setPreview(null);
    };

    const handleRowMouseEnter = (event, item, fileName) => {
        if (!enabled) return;

        clearPreviewTimer();
        detachRowTracking();

        const row = event.currentTarget;
        cursorRef.current = {x: event.clientX, y: event.clientY};

        const onMouseMove = (mouseEvent) => {
            cursorRef.current = {x: mouseEvent.clientX, y: mouseEvent.clientY};
        };

        moveHandlerRef.current = onMouseMove;
        activeRowRef.current = row;
        row.addEventListener("mousemove", onMouseMove);

        const isImage = isImageAttachment(item, fileName);

        timerRef.current = setTimeout(() => {
            setPreview({
                item,
                fileName,
                cursor: {...cursorRef.current},
                kind: isImage ? "image" : "file",
                status: isImage ? "loading" : "ready",
                imageUrl: null,
            });
        }, HOVER_PREVIEW_DELAY_MS);
    };

    const handleRowMouseLeave = () => {
        closePreview();
    };

    useEffect(() => () => {
        clearPreviewTimer();
        detachRowTracking();
    }, []);

    return {
        preview,
        setPreview,
        closePreview,
        handleRowMouseEnter,
        handleRowMouseLeave,
    };
}

export default AttachmentHoverPreviewPortal;
